import type { Cache } from '@urql/exchange-graphcache';

import {
  type CartDataFragment,
  type SubmitBagOrderWithPaymentIntentMutation,
  type SubmitBagOrderWithPaymentMethodMutation,
} from '../../../../../graphql/operations.generated';
import { CartDocument } from '../../../../../hooks/useCart/GraphQL/Cart.generated';
import { LastOrderDateDocument } from '../../../../../hooks/usePostOrder';

// ────────────────────────────────────────────────────────────────────────────────

export function updateCartCache(
  cache: Cache,
  responseWithCart: MutationResponseWithCart,
) {
  if (!responseWithCart.cart) return;

  cache.updateQuery({ query: CartDocument }, (data) => {
    const shouldUpdateCache = data && responseWithCart.cart;

    if (shouldUpdateCache) data.cart = responseWithCart.cart;

    return data;
  });
}

export function invalidateCartCacheEntry(
  cache: Cache,
  response:
    | SubmitBagOrderWithPaymentMethodMutation['submitOrderUsingPaymentMethod']
    | SubmitBagOrderWithPaymentIntentMutation['submitOrderUsingPaymentIntent'],
) {
  const result = response?.__typename;
  const isSubmitted =
    result === 'SubmitOrderUsingPaymentMethodSuccess' ||
    result === 'SubmitOrderUsingPaymentIntentSuccess';

  if (isSubmitted) {
    cache.updateQuery({ query: CartDocument }, (data) => {
      if (data) data.cart = null;

      return data;
    });
  }
}

export function updateOrderPostSubmission(
  cache: Cache,
  response:
    | SubmitBagOrderWithPaymentMethodMutation['submitOrderUsingPaymentMethod']
    | SubmitBagOrderWithPaymentIntentMutation['submitOrderUsingPaymentIntent'],
) {
  const result = response?.__typename;
  const isSubmitted =
    result === 'SubmitOrderUsingPaymentMethodSuccess' ||
    result === 'SubmitOrderUsingPaymentIntentSuccess';

  if (isSubmitted) {
    cache.updateQuery(
      {
        query: LastOrderDateDocument,
        variables: {
          input: {
            page: 1,
            take: 1,
          },
        },
      },
      (data) => {
        const lastOrderResponse = {
          __typename: 'OrdersResponseSuccess',
          orders: {
            __typename: 'OrdersResponseSuccess',
            orders: [response.order],
          },
        };

        data = lastOrderResponse;

        return data;
      },
    );
  }
}

// ─── TYPES ──────────────────────────────────────────────────────────────────────

export type MutationResponseWithCart = Readonly<{
  cart?: Partial<CartDataFragment>;
  __typename?: string;
}>;
